import { Link } from "react-router-dom";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import { Panel, Stat, StatusBadge } from "@/components/shell/page";
import { formatDateDisplay } from "@/lib/timezone";

export function EmployeeDashboardView({
  data,
  employeePath = "/employee",
}: {
  data: any;
  employeePath?: string;
}) {
  const profile = data.profile ?? {};
  const tasks = data.tasks ?? {};
  const leave = data.leave?.items ?? [];
  const hours = data.hours ?? {};
  const performance = data.performance ?? {};
  const awards = data.awards ?? [];
  const attendance = data.attendance ?? {};
  const own = employeePath === "/employee";

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <header>
        <p className="text-sm text-muted-foreground">{formatDateDisplay(data.date)}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {profile.name || "Today"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {[profile.employee_code, profile.designation, profile.department_name]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Tasks done"
          value={`${tasks.completion_pct ?? 0}%`}
          hint={`${tasks.completed ?? 0} complete · ${tasks.pending ?? 0} still open`}
        />
        <Stat
          label="Hours today"
          value={hours.today ?? 0}
          hint={`Week ${hours.week ?? 0} of ${hours.expected_week ?? 40}`}
        />
        <Stat
          label="Points"
          value={performance.total ?? 0}
          hint={`This month ${performance.month ?? 0}`}
        />
        <Stat
          label="Leave available"
          value={leave.reduce((sum: number, item: any) => sum + Number(item.available || 0), 0)}
          hint={leave.length ? `${leave.length} types enabled` : "No leave types enabled"}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <Panel title="Punch attendance" className="lg:col-span-3" description="From the biometric devices. This portal does not edit punches.">
          {!attendance.linked && (
            <p className="text-sm text-muted-foreground">
              No device PIN is linked yet. Punches appear after an employer attaches the PIN.
            </p>
          )}
          <div className="divide-y">
            {(attendance.sessions ?? []).map((row: any) => (
              <div key={row.session} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                <span className="text-sm font-medium">{row.label}</span>
                <AttendanceCellBadge
                  showWord
                  cell={{
                    status: row.status,
                    late_minutes: row.late_minutes,
                    first_punch_at: row.first_punch_at,
                  }}
                />
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Leave balances" className="lg:col-span-2">
          {leave.length === 0 && (
            <p className="text-sm text-muted-foreground">No leave types are enabled.</p>
          )}
          <ul className="space-y-3">
            {leave.map((item: any) => (
              <li key={item.id} className="flex items-baseline justify-between gap-3 text-sm">
                <span>{item.name}</span>
                <span className="font-medium tabular-nums">{item.available}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {(profile.responsibilities?.length > 0 || awards.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {profile.responsibilities?.length > 0 && (
            <Panel title="Responsibilities">
              <ul className="space-y-2 text-sm">
                {profile.responsibilities.map((item: any) => (
                  <li key={item.id} className="rounded-lg bg-muted px-3 py-2">
                    {item.title}
                  </li>
                ))}
              </ul>
            </Panel>
          )}
          {awards.length > 0 && (
            <Panel title="Awards">
              <ul className="space-y-3">
                {awards.map((award: any) => (
                  <li key={award.id} className="flex items-center justify-between gap-3 text-sm">
                    <div>
                      <p className="font-medium">{award.name}</p>
                      <p className="text-muted-foreground">{award.awarded_on}</p>
                    </div>
                    <StatusBadge status={`+${award.points}`} />
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      )}

      {own && (
        <div className="flex flex-wrap gap-2">
          <Link className="inline-flex min-h-11 cursor-pointer items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" to="/employee/tasks">
            Open tasks
          </Link>
          <Link className="inline-flex min-h-11 cursor-pointer items-center rounded-lg border bg-card px-4 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" to="/employee/leave">
            Request leave
          </Link>
          <Link className="inline-flex min-h-11 cursor-pointer items-center rounded-lg border bg-card px-4 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" to="/employee/hours">
            Log hours
          </Link>
        </div>
      )}
    </div>
  );
}
